
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Sorting} from './Sorting';
import {FieldModelSorting} from './Sorting';

/**
 * ManagerSorting struct
 */
class ManagerSorting {
  /**
   * Initialize struct
   * @param {Sorting=} login
   * @constructor
   */
  constructor (login = null) {
    this.Login = login
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ManagerSorting}
   * @param {!ManagerSorting} other Other struct
   * @returns {!ManagerSorting} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = Sorting.fromObject(other.Login)
    } else {
      this.Login = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ManagerSorting}
   * @returns {!ManagerSorting} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ManagerSortingModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ManagerSortingModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ManagerSorting}
   * @param {!ManagerSorting} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ManagerSorting)) {
      throw new TypeError('Instance of ManagerSorting is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ManagerSorting}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ManagerSorting.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ManagerSorting} other Object value
   * @returns {!ManagerSorting} Created struct
   */
  static fromObject (other) {
    return new ManagerSorting().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ManagerSorting}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ManagerSorting.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ManagerSorting}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 159
  }
}

export { ManagerSorting };

/**
 * Fast Binary Encoding ManagerSorting field model
 */
class FieldModelManagerSorting extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelOptional(new FieldModelSorting(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelManagerSorting.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 159
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelManagerSorting}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelManagerSorting}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelManagerSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelManagerSorting}
   * @param {!ManagerSorting} fbeValue Default value, defaults is new ManagerSorting()
   * @returns {!ManagerSorting} ManagerSorting value
   */
  get (fbeValue = new ManagerSorting()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelManagerSorting}
   * @param {!ManagerSorting} fbeValue ManagerSorting value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelManagerSorting}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelManagerSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelManagerSorting}
   * @param {!ManagerSorting} fbeValue ManagerSorting value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelManagerSorting}
   * @param {ManagerSorting} fbeValue ManagerSorting value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
  }
}

export { FieldModelManagerSorting };

/**
 * Fast Binary Encoding ManagerSorting model
 */
class ManagerSortingModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelManagerSorting(this.buffer, 4)
  }

  /**
   * Get the ManagerSorting model
   * @this {!ManagerSortingModel}
   * @returns {!FieldModelManagerSorting} model ManagerSorting model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ManagerSortingModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ManagerSortingModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ManagerSortingModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ManagerSortingModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelManagerSorting.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ManagerSortingModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ManagerSortingModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ManagerSortingModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ManagerSortingModel}
   * @param {!ManagerSorting} value ManagerSorting value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ManagerSortingModel}
   * @param {!ManagerSorting} value ManagerSorting value, defaults is new ManagerSorting()
   * @return {!object} Deserialized ManagerSorting value and its size
   */
  deserialize (value = new ManagerSorting()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ManagerSorting(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ManagerSorting(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ManagerSortingModel}
   * @param {!number} prev Previous ManagerSorting model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ManagerSortingModel };
